import { CalendarDate, MealType } from '@talktoast/shared'
import { MEAL_TYPE_ICONS, getMealEmoji } from '../constants/uiConstants'

export interface WeekSummary {
  date: string // YYYY-MM-DD format
  mealCount: number
  mealTypes?: MealType[] // Meal types for this day
}

export interface WeeklyPreviewProps {
  /**
   * Array of 7 dates representing the week
   */
  weekDates: CalendarDate[]

  /**
   * Meal counts for each day
   */
  weekSummary: WeekSummary[]

  /**
   * Currently selected date (for visual highlighting)
   */
  selectedDate?: string

  /**
   * Callback when a day is clicked
   */
  onDayClick: (date: string) => void

  /**
   * Additional CSS classes
   */
  className?: string
}

/**
 * WeeklyPreview Component
 * Displays a 7-day horizontal view with meal counts
 * Pattern: Compact Week Bar (used by Mealime, Eat This Much, PlateJoy)
 */
export default function WeeklyPreview({
  weekDates,
  weekSummary,
  selectedDate,
  onDayClick,
  className = ''
}: WeeklyPreviewProps) {
  // Create a map for O(1) lookup of meal data by date string
  const mealDataMap = new Map(
    weekSummary.map(day => [day.date, { count: day.mealCount, types: day.mealTypes || [] }])
  )

  const getDayClasses = (dateStr: string, isToday: boolean, mealCount: number): string => {
    const baseClasses = 'flex flex-col items-center p-2 sm:p-3 rounded-lg transition-all cursor-pointer'
    const isSelected = dateStr === selectedDate

    // Selected day (gold background + white text)
    if (isSelected) {
      return `${baseClasses} bg-toast-gold text-white`
    }

    // Today (not selected) - gold ring outline with offset to prevent crop
    if (isToday) {
      return `${baseClasses} bg-white ring-2 sm:ring-4 ring-toast-gold hover:bg-gray-50`
    }

    // Days with meals - white background
    if (mealCount > 0) {
      return `${baseClasses} bg-white border border-gray-200 hover:bg-gray-50`
    }

    // Empty days - gray background
    return `${baseClasses} bg-gray-50 border border-gray-300 hover:border-gray-400`
  }

  const formatDateString = (calendarDate: CalendarDate): string => {
    // Convert CalendarDate to YYYY-MM-DD format
    const year = calendarDate.year
    const month = String(calendarDate.month + 1).padStart(2, '0') // month is 0-indexed
    const day = String(calendarDate.date).padStart(2, '0')
    return `${year}-${month}-${day}`
  }

  return (
    <div className={`w-full ${className}`}>
      {/* 7-day grid - uses CSS grid to fit all 7 days without scrolling */}
      <div className="grid grid-cols-7 gap-1 sm:gap-2">
        {weekDates.map((day) => {
          const dateStr = formatDateString(day)
          const mealData = mealDataMap.get(dateStr) || { count: 0, types: [] }
          const isSelected = dateStr === selectedDate
          const classes = getDayClasses(dateStr, day.isToday, mealData.count)

          // Get unique meal type emojis (show 1-3, then "4+" for more)
          const uniqueMealTypes = Array.from(new Set(mealData.types))
          const displayMealTypes = uniqueMealTypes.slice(0, 3)
          const hasMoreMeals = uniqueMealTypes.length > 3

          return (
            <button
              key={day.key}
              onClick={() => onDayClick(dateStr)}
              className={classes}
              aria-label={`${day.fullLabel}, ${mealData.count} meals planned`}
            >
              {/* Day label */}
              <span className={`text-xs sm:text-caption mb-0.5 sm:mb-1 ${isSelected ? 'text-white' : 'text-gray-600'}`}>
                {day.label}
              </span>

              {/* Date number */}
              <span className={`text-base sm:text-h3 font-bold ${isSelected ? 'text-white' : 'text-gray-900'}`}>
                {day.date}
              </span>

              {/* Meal icons (show 1-3 icons, then "4+" for more) */}
              {mealData.count > 0 && (
                <div className="flex gap-0.5 sm:gap-1 mt-1 sm:mt-2 items-center justify-center flex-wrap">
                  {displayMealTypes.map((mealType, i) => (
                    <span key={i} className="text-xs">
                      {getMealEmoji(mealType)}
                    </span>
                  ))}
                  {hasMoreMeals && (
                    <span className={`text-[10px] sm:text-xs font-medium ${isSelected ? 'text-white' : 'text-gray-600'}`}>
                      4+
                    </span>
                  )}
                </div>
              )}
            </button>
          )
        })}
      </div>
    </div>
  )
}
